import React from 'react';
import { industries } from '../../../data/settingsData';

const FormField = ({ label, required, children }) => (
  <div className="mb-6">
    <label className="block text-[11px] font-black text-slate-400 mb-2 uppercase tracking-[0.2em]">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    {children}
  </div>
);

export default function ProfileTab({ data, onChange }) {
  const entityTypes = [
    "Sole Proprietorship",
    "Partnership",
    "Limited Liability Company (LLC)",
    "C-Corporation",
    "S-Corporation",
    "Non-Profit"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value });
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-400">
      <div className="mb-10">
        <h2 className="text-xl font-bold text-slate-900 mb-2">Business Profile</h2>
        <p className="text-sm text-slate-500">Update your official business identification and contact details.</p>
      </div>
      
      <div className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <FormField label="Display Name" required>
            <input 
              type="text" 
              name="name"
              value={data.name}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" 
              placeholder="ProfitYo Inc." 
            />
          </FormField>

          <FormField label="Legal Name">
            <input 
              type="text" 
              name="legal_name"
              value={data.legal_name}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all" 
              placeholder="ProfitYo Software Solutions LLC" 
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <FormField label="Entity Type" required>
            <div className="relative">
              <select 
                name="entity_type"
                value={data.entity_type}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none appearance-none cursor-pointer"
              >
                <option value="">Select entity type</option>
                {entityTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </div>
            </div>
          </FormField>

          <FormField label="Industry" required>
            <div className="relative">
              <select 
                name="industry"
                value={data.industry}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none appearance-none cursor-pointer"
              >
                <option value="">Select industry</option>
                {industries.map(i => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </div>
            </div>
          </FormField>
        </div>

        <FormField label="Business Address">
          <textarea 
            name="address"
            value={data.address}
            onChange={handleChange}
            rows="3"
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all resize-none" 
            placeholder="123 Financial Way, Suite 100&#10;San Francisco, CA 94105"
          />
        </FormField>

        <div className="mt-12 pt-10 border-t border-slate-100">
          <div className="mb-8">
            <h3 className="text-[14px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Contact Details</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <FormField label="Public Email" required>
              <input 
                type="email" 
                name="email"
                value={data.email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none" 
                placeholder="billing@company.com" 
              />
            </FormField>

            <FormField label="Phone Number">
              <input 
                type="tel" 
                name="phone"
                value={data.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none" 
                placeholder="+1 (555) 000-0000" 
              />
            </FormField>
          </div>

          <FormField label="Website">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[14px] font-bold">https://</span>
              <input 
                type="text" 
                name="website"
                value={data.website}
                onChange={handleChange}
                className="w-full pl-16 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-[14px] font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none" 
                placeholder="www.yourbusiness.com" 
              />
            </div>
          </FormField>
        </div>
      </div>
    </div>
  );
}